// src/Admin/AddProduct.jsx
import { useState } from "react";

export default function AddProduct() {
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    offer_price: "",
    colors: [],
    sizes: [],
    stock: "",
  });
  const [image, setImage] = useState(null);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const colorsList = ["White", "Black", "Blue", "Red", "Pink", "Green", "Yellow"];
  const sizesList = ["S", "M", "L", "XL", "XXL", "XXXL"];

  const toggleSelect = (value, field) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const data = new FormData();
    data.append("name", formData.name);
    data.append("price", formData.price);
    data.append("offer_price", formData.offer_price || "");
    data.append("stock", formData.stock);
    formData.colors.forEach(color => data.append("colors[]", color));
    formData.sizes.forEach(size => data.append("sizes[]", size));
    if (image) data.append("image", image);

    try {
      const response = await fetch("/api/add-product/", {
        method: "POST",
        body: data,
      });

      if (response.ok) {
        setMessage("Product added successfully!");
        setFormData({
          name: "", price: "", offer_price: "", colors: [], sizes: [], stock: ""
        });
        setImage(null);
        document.querySelector('input[type="file"]').value = "";
        setTimeout(() => setMessage(""), 3000);
      } else {
        setMessage("Failed to add product.");
      }
    } catch (error) {
      setMessage("Something went wrong.");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
            Add New Product
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Product Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Product Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                placeholder="Enter product name"
              />
            </div>

            {/* Price & Offer Price */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Price (₹)</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                  placeholder="999"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Offer Price (₹)</label>
                <input
                  type="number"
                  name="offer_price"
                  value={formData.offer_price}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                  placeholder="799 (optional)"
                />
              </div>
            </div>

            {/* Stock */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Available Stock</label>
              <input
                type="number"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500"
                placeholder="50"
              />
            </div>

            {/* Colors - Checkbox Style */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Select Colors</label>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {colorsList.map(color => (
                  <label
                    key={color}
                    className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-300 hover:bg-gray-50 transition"
                  >
                    <input
                      type="checkbox"
                      checked={formData.colors.includes(color)}
                      onChange={() => toggleSelect(color, "colors")}
                      className="w-5 h-5 text-gray-700 rounded focus:ring-gray-500"
                    />
                    <span className="text-sm font-medium text-gray-800">{color}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Sizes - Checkbox Style */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Select Sizes</label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                {sizesList.map(size => (
                  <label
                    key={size}
                    className="flex items-center justify-center cursor-pointer p-3 rounded-lg border border-gray-300 hover:bg-gray-50 transition"
                  >
                    <input
                      type="checkbox"
                      checked={formData.sizes.includes(size)}
                      onChange={() => toggleSelect(size, "sizes")}
                      className="sr-only"
                    />
                    <span
                      className={`text-sm font-bold ${formData.sizes.includes(size)
                        ? "text-white bg-gray-800"
                        : "text-gray-700"
                        } px-4 py-2 rounded-md transition`}
                    >
                      {size}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Product Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setImage(e.target.files[0])}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-gray-800 file:text-white hover:file:bg-gray-700"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-4 rounded-lg font-bold text-white transition-all ${
                isSubmitting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-gray-800 hover:bg-gray-900 shadow-lg"
              }`}
            >
              {isSubmitting ? "Saving Product..." : "Save Product"}
            </button>
          </form>

          {/* Success Message */}
          {message && (
            <div className={`mt-6 p-4 rounded-lg text-center font-semibold ${
              message.includes("success")
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}>
              {message}
            </div>
          )}

          <div className="mt-8 text-center">
            <a href="/admin-dashboard" className="text-gray-600 hover:text-gray-800 underline">
              Back to Dashboard
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}